import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { talentAPI, bookmarkAPI, userHideAPI } from '../../services/api';
import chatService from '../../services/chatService';
import { useChatPopup } from '../../contexts/ChatPopupContext';
import BookmarkModal from './BookmarkModal';
import ReportUserModal from './ReportUserModal';
import '../Jobs/JobsPage.css';
import '../PublicProfile.css';

const TalentDetailPanel = ({ talent, currentUser, isAuthenticated, onUserReportSuccess, isHidden: isHiddenProp, onUnhideUser }) => {
  const navigate = useNavigate();
  const { openChatPopup, updatePopupRoom } = useChatPopup();
  const [talentDetails, setTalentDetails] = useState(talent || null);
  const [loading, setLoading] = useState(false);
  const [showBookmarkModal, setShowBookmarkModal] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkLists, setBookmarkLists] = useState([]);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isHidden, setIsHidden] = useState(isHiddenProp || false);
  const [unhiding, setUnhiding] = useState(false);

  // Check if the current user is viewing their own profile
  const isOwnProfile = React.useMemo(() => {
    if (!currentUser || !talentDetails) return false;
    
    const isOwn = (
      currentUser.id === talentDetails.id ||
      currentUser.email === talentDetails.email ||
      (currentUser.username && currentUser.username === talentDetails.username)
    );
    
    // Debug logging
    console.log('TalentDetailPanel - isOwnProfile check:', {
      currentUserId: currentUser.id,
      talentId: talentDetails.id,
      currentUserEmail: currentUser.email,
      talentEmail: talentDetails.email,
      currentUserUsername: currentUser.username,
      talentUsername: talentDetails.username,
      isOwn
    });
    
    return isOwn;
  }, [currentUser, talentDetails]);

  useEffect(() => {
    setIsHidden(isHiddenProp || false);
  }, [isHiddenProp]);

  useEffect(() => {
    if (talent) {
      setTalentDetails(talent);
      // Debug: Log full talent data to verify all fields are present
      console.log('TalentDetailPanel - Full Talent Data:', {
        name: talent.name,
        username: talent.username,
        profile_photo_url: talent.profile_photo_url,
        banner_image_url: talent.banner_image_url,
        experiences: talent.experiences,
        experiencesType: typeof talent.experiences,
        experiencesIsArray: Array.isArray(talent.experiences),
        experiencesCount: Array.isArray(talent.experiences) ? talent.experiences.length : 'N/A',
        projects: talent.projects,
        projectsType: typeof talent.projects,
        projectsIsArray: Array.isArray(talent.projects),
        projectsCount: Array.isArray(talent.projects) ? talent.projects.length : 'N/A',
        education: talent.education,
        educationType: typeof talent.education,
        educationIsArray: Array.isArray(talent.education),
        educationCount: Array.isArray(talent.education) ? talent.education.length : 'N/A',
        skills: talent.skills,
        skillsType: typeof talent.skills,
        skillsIsArray: Array.isArray(talent.skills),
        skillsCount: Array.isArray(talent.skills) ? talent.skills.length : 'N/A',
        fullTalentObject: talent
      });
    }
  }, [talent]);

  // Check if talent is bookmarked
  useEffect(() => {
    if (talentDetails?.id && isAuthenticated && !isOwnProfile) {
      checkBookmarkStatus();
    }
  }, [talentDetails?.id, isAuthenticated, isOwnProfile]);

  const checkBookmarkStatus = async () => {
    try {
      const response = await bookmarkAPI.checkBookmarked(talentDetails.id);
      if (response.success) {
        setIsBookmarked(response.is_bookmarked);
        setBookmarkLists(response.lists || []);
      }
    } catch (err) {
      console.error('Error checking bookmark status:', err);
    }
  };

  const handleBookmarkClick = () => {
    if (!isOwnProfile && isAuthenticated) {
      setShowBookmarkModal(true);
    }
  };

  const handleBookmarked = (listId) => {
    setIsBookmarked(true);
    checkBookmarkStatus();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Present';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short'
    });
  };

  const formatFullDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleViewFullProfile = () => {
    if (talentDetails?.username) {
      window.open(`/talent/${talentDetails.username}`, '_blank', 'noopener,noreferrer');
    }
  };

  const handleUnhideUser = async () => {
    if (!talentDetails?.id) return;
    
    setUnhiding(true);
    try {
      const response = await userHideAPI.unhide(talentDetails.id);
      if (response.success) {
        setIsHidden(false);
        // Call parent callback if provided
        if (onUnhideUser) {
          onUnhideUser(talentDetails.id);
        }
      } else {
        console.error('Failed to unhide user:', response.message);
      }
    } catch (error) {
      console.error('Error unhiding user:', error);
    } finally {
      setUnhiding(false);
    }
  };

  // Get achievement badge similar to PublicProfile - using useMemo to recalculate when talentDetails changes
  const achievementBadge = React.useMemo(() => {
    if (!talentDetails) return null;

    // Use exact same logic as PublicProfile - check if arrays exist and have length
    const hasProjects = Array.isArray(talentDetails.projects) && talentDetails.projects.length > 0;
    const hasExperience = Array.isArray(talentDetails.experiences) && talentDetails.experiences.length > 0;
    const hasSkills = Array.isArray(talentDetails.skills) && talentDetails.skills.length > 0;
    const hasEducation = Array.isArray(talentDetails.education) && talentDetails.education.length > 0;

    // Debug: Log badge calculation data
    console.log('TalentDetailPanel - Badge calculation:', {
      hasProjects,
      projectsCount: Array.isArray(talentDetails.projects) ? talentDetails.projects.length : 0,
      hasExperience,
      experiencesCount: Array.isArray(talentDetails.experiences) ? talentDetails.experiences.length : 0,
      hasSkills,
      skillsCount: Array.isArray(talentDetails.skills) ? talentDetails.skills.length : 0,
      hasEducation,
      educationCount: Array.isArray(talentDetails.education) ? talentDetails.education.length : 0,
      experiences: talentDetails.experiences,
      experiencesType: typeof talentDetails.experiences,
      experiencesIsArray: Array.isArray(talentDetails.experiences),
      projects: talentDetails.projects,
      skills: talentDetails.skills,
      education: talentDetails.education
    });

    if (hasExperience) {
      return (
        <div className="achievement-badge legend-badge">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
            <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
            <path d="M4 22h16"></path>
            <path d="M10 14.66V17c0 .55-.47.98-.97 1.21l-1.25.5c-.3.12-.5.42-.5.74 0 .32.2.62.5.74l1.25.5c.5.23.97.66.97 1.21v2.34"></path>
            <path d="M14 14.66V17c0 .55.47.98.97 1.21l1.25.5c.3.12.5.42.5.74 0 .32-.2.62-.5.74l-1.25.5c-.5.23-.97.66-.97 1.21v2.34"></path>
            <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
          </svg>
          <span>Legend</span>
        </div>
      );
    }
    if (hasProjects) {
      return (
        <div className="achievement-badge shining-star-badge">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"></polygon>
          </svg>
          <span>Shining Star</span>
        </div>
      );
    }
    if (hasSkills || hasEducation) {
      return (
        <div className="achievement-badge rising-star-badge">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
          </svg>
          <span>Rising Star</span>
        </div>
      );
    }
    return (
      <div className="achievement-badge newcomer-badge">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M12 6v6l4 2"></path>
        </svg>
        <span>Newcomer</span>
      </div>
    );
  }, [talentDetails]);

  if (loading) {
    return (
      <div className="jobs-loading" style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="jobs-spinner"></div>
        <span>Loading profile...</span>
      </div>
    );
  }

  if (!talentDetails) {
    return (
      <div className="jobs-no-selection">
        <p>No talent selected</p>
      </div>
    );
  }

  return (
    <div className="public-job-detail-view" style={{ background: '#f3f2ef', minHeight: '100%', overflowY: 'auto' }}>
      {/* Responsive Styles - Simple fix to prevent jumbling on mobile only */}
      <style>{`
        @media (max-width: 768px) {
          .talent-action-buttons {
            width: 100% !important;
            margin-top: 12px !important;
            justify-content: flex-start !important;
          }
          .name-title-container {
            width: 100% !important;
            padding-right: 0 !important;
            margin-bottom: 8px;
          }
        }
        @media (min-width: 769px) {
          .talent-action-buttons {
            width: auto !important;
            margin-top: 0 !important;
            flex-shrink: 0 !important;
          }
        }
      `}</style>
      {/* LinkedIn-style Profile Layout */}
      <div className="linkedin-profile" style={{ background: 'transparent', padding: '0' }}>
        {/* Cover Photo Section - Increased Height by 10% */}
        <div className="cover-photo-section" style={{ height: '132px', marginBottom: '0', position: 'relative' }}>
          {/* Achievement Badge - Top Right Corner of Banner */}
          {achievementBadge && (
            <div style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 10 }}>
              {achievementBadge}
            </div>
          )}
          
          <div className="cover-photo" style={{ height: '100%' }}>
            {talentDetails.banner_image_url ? (
              <img 
                src={talentDetails.banner_image_url} 
                alt={`${talentDetails.name} cover photo`} 
                className="cover-image"
                loading="eager"
                decoding="async"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="cover-placeholder" style={{ height: '100%', background: '#e5e7eb' }}>
                <div className="cover-pattern"></div>
              </div>
            )}
          </div>
          
          {/* Profile Picture - Smaller, No Gradient */}
          <div className="profile-picture-section" style={{ bottom: '-40px' }}>
            <div className="profile-picture-container">
              {talentDetails.profile_photo_url ? (
                <img 
                  src={talentDetails.profile_photo_url} 
                  alt={`${talentDetails.name} profile picture`} 
                  className="profile-picture"
                  style={{ width: '100px', height: '100px', border: '3px solid white' }}
                  loading="eager"
                  decoding="async"
                />
              ) : (
                <div className="profile-picture-placeholder" style={{ 
                  width: '100px', 
                  height: '100px', 
                  border: '3px solid white',
                  background: '#d1d5db',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                }}>
                  <span style={{ fontSize: '36px', fontWeight: 600, color: '#6b7280' }}>
                    {talentDetails.name?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Hidden User Alert - Show to user who hid the user */}
        {isAuthenticated && isHidden && (
          <div className="job-hidden-alert" style={{ margin: '20px 24px' }}>
            <div className="alert-content">
              <div className="alert-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              </div>
              <div className="alert-text">
                <h4>This user is hidden for you</h4>
                <p>You previously hid this user. You can unhide them to see them in your listings again.</p>
              </div>
              <button 
                onClick={handleUnhideUser}
                className="unhide-job-btn"
                disabled={unhiding}
              >
                {unhiding ? (
                  <>
                    <div className="spinner-small"></div>
                    Unhiding...
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                    Unhide User
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Profile Header - No White Background */}
        <div className="profile-header-section" style={{ padding: '60px 0 20px 0', marginTop: '0', borderBottom: 'none', position: 'relative' }}>
          <div className="profile-header-content" style={{ padding: '0 24px' }}>
            <div className="profile-info">
              {/* Name, Title, and View Full Profile Button - Responsive Row */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                justifyContent: 'space-between', 
                gap: '16px', 
                marginBottom: '12px', 
                flexWrap: 'wrap', 
                width: '100%'
              }}>
                <div className="name-title-container" style={{ 
                  flex: '1 1 auto', 
                  minWidth: '200px',
                  paddingRight: '16px'
                }}>
                  <h1 className="profile-name" style={{ 
                    fontSize: '24px', 
                    fontWeight: 700, 
                    color: '#000', 
                    margin: 0, 
                    lineHeight: '1.3',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word'
                  }}>
                    {talentDetails.name}
                    {talentDetails.professional_title && (
                      <>
                        <span style={{ margin: '0 6px', color: '#9ca3af', fontWeight: 400 }}> - </span>
                        <span style={{ fontSize: '24px', fontWeight: 500, color: '#666' }}>
                          {talentDetails.professional_title}
                        </span>
                      </>
                    )}
                  </h1>
                  {!talentDetails.professional_title && (
                    <div className="professional-title-placeholder" style={{ marginTop: '4px' }}>
                      <span>Professional</span>
                    </div>
                  )}
                </div>
                
                {/* Action Buttons - Responsive Positioning */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  flexShrink: 0,
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end'
                }}
                className="talent-action-buttons"
                >
                  {/* View Full Profile Button - No Background */}
                  <button 
                    onClick={handleViewFullProfile}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 16px',
                      background: 'transparent',
                      color: '#7a5fb8',
                      border: '1px solid #7a5fb8',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#f5f3ff';
                      e.target.style.borderColor = '#6b4fa0';
                      e.target.style.color = '#6b4fa0';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'transparent';
                      e.target.style.borderColor = '#7a5fb8';
                      e.target.style.color = '#7a5fb8';
                    }}
                  >
                    View Full Profile
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      <polyline points="15,3 21,3 21,9"></polyline>
                      <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                  </button>
                  
                  {/* Chat Button */}
                  <button 
                    onClick={async (e) => {
                      if (isOwnProfile) {
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                      }
                      
                      e.preventDefault();
                      e.stopPropagation();
                      
                      // Open popup immediately - it will load data in background
                      openChatPopup(
                        talentDetails.id,
                        talentDetails.name,
                        talentDetails.profile_photo_url,
                        null // Will be loaded in background by popup
                      );
                      
                      // Pre-fetch chat info in background (non-blocking) to update popup
                      chatService.getPeerToPeerChatInfo(talentDetails.id)
                        .then(response => {
                          if (response.success && response.data && response.data.room) {
                            // Update popup with room data if available
                            updatePopupRoom(talentDetails.id, response.data.room);
                          }
                        })
                        .catch(err => {
                          console.error('Error pre-fetching chat:', err);
                          // Popup will handle loading on its own
                        });
                    }}
                    disabled={isOwnProfile}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '40px',
                      height: '40px',
                      padding: 0,
                      background: isOwnProfile ? '#f3f4f6' : 'transparent',
                      color: isOwnProfile ? '#9ca3af' : '#7a5fb8',
                      border: `1px solid ${isOwnProfile ? '#d1d5db' : '#7a5fb8'}`,
                      borderRadius: '6px',
                      cursor: isOwnProfile ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      flexShrink: 0,
                      opacity: isOwnProfile ? 0.6 : 1,
                      pointerEvents: isOwnProfile ? 'none' : 'auto'
                    }}
                    onMouseEnter={(e) => {
                      if (!isOwnProfile) {
                        e.currentTarget.style.background = '#f5f3ff';
                        e.currentTarget.style.borderColor = '#6b4fa0';
                        e.currentTarget.style.color = '#6b4fa0';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isOwnProfile) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.borderColor = '#7a5fb8';
                        e.currentTarget.style.color = '#7a5fb8';
                      }
                    }}
                    title={isOwnProfile ? "Cannot chat with yourself" : "Chat with this talent"}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                  </button>
                  
                  {/* Save/Bookmark Button - Blue */}
                  <button 
                    onClick={handleBookmarkClick}
                    disabled={isOwnProfile || !isAuthenticated}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '40px',
                      height: '40px',
                      padding: 0,
                      background: (isOwnProfile || !isAuthenticated) ? '#f3f4f6' : (isBookmarked ? '#e0f2fe' : 'transparent'),
                      color: (isOwnProfile || !isAuthenticated) ? '#9ca3af' : (isBookmarked ? '#0369a1' : '#0073b1'),
                      border: `1px solid ${(isOwnProfile || !isAuthenticated) ? '#d1d5db' : (isBookmarked ? '#0369a1' : '#0073b1')}`,
                      borderRadius: '6px',
                      cursor: (isOwnProfile || !isAuthenticated) ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      flexShrink: 0,
                      opacity: (isOwnProfile || !isAuthenticated) ? 0.6 : 1,
                      pointerEvents: (isOwnProfile || !isAuthenticated) ? 'none' : 'auto'
                    }}
                    onMouseEnter={(e) => {
                      if (!isOwnProfile && isAuthenticated) {
                        e.currentTarget.style.background = '#f0f7fa';
                        e.currentTarget.style.borderColor = '#006ba1';
                        e.currentTarget.style.color = '#006ba1';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isOwnProfile && isAuthenticated) {
                        e.currentTarget.style.background = isBookmarked ? '#e0f2fe' : 'transparent';
                        e.currentTarget.style.borderColor = isBookmarked ? '#0369a1' : '#0073b1';
                        e.currentTarget.style.color = isBookmarked ? '#0369a1' : '#0073b1';
                      }
                    }}
                    title={isOwnProfile ? "Cannot save your own profile" : (isBookmarked ? "Already bookmarked" : "Save talent for future consideration")}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      {isBookmarked ? (
                        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" fill="currentColor"></path>
                      ) : (
                        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
                      )}
                    </svg>
                  </button>
                  
                  {/* Report Button - Red/Maroon */}
                  <button 
                    onClick={(e) => {
                      if (isOwnProfile) {
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                      }
                      setShowReportModal(true);
                    }}
                    disabled={isOwnProfile}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '40px',
                      height: '40px',
                      padding: 0,
                      background: isOwnProfile ? '#f3f4f6' : 'transparent',
                      color: isOwnProfile ? '#9ca3af' : '#991b1b',
                      border: `1px solid ${isOwnProfile ? '#d1d5db' : '#991b1b'}`,
                      borderRadius: '6px',
                      cursor: isOwnProfile ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      flexShrink: 0,
                      opacity: isOwnProfile ? 0.6 : 1,
                      pointerEvents: isOwnProfile ? 'none' : 'auto'
                    }}
                    onMouseEnter={(e) => {
                      if (!isOwnProfile) {
                        e.currentTarget.style.background = '#fef2f2';
                        e.currentTarget.style.borderColor = '#7f1d1d';
                        e.currentTarget.style.color = '#7f1d1d';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isOwnProfile) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.borderColor = '#991b1b';
                        e.currentTarget.style.color = '#991b1b';
                      }
                    }}
                    title={isOwnProfile ? "Cannot report your own profile" : "Report this profile"}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                  </button>
                </div>
              </div>
              
              {(talentDetails.work_type || talentDetails.job_seeking_status || talentDetails.location) && (
                <div className="profile-details-section" style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '12px', 
                  alignItems: 'center',
                  marginTop: '12px'
                }}>
                  {talentDetails.job_seeking_status && (
                    <span className={`job-seeking-status ${talentDetails.job_seeking_status === 'actively-seeking' ? 'actively-seeking' : ''}`}>
                      {talentDetails.job_seeking_status === 'actively-seeking' ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                          <path d="M8 12l2 2 4-4"></path>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 12l2 2 4-4"></path>
                          <path d="M21 12c.552 0 1-.448 1-1V5c0-.552-.448-1-1-1H3c-.552 0-1 .448-1 1v6c0 .552.448 1 1 1h18z"></path>
                          <path d="M3 12v6c0 .552.448 1 1 1h16c.552 0 1-.448 1-1v-6"></path>
                        </svg>
                      )}
                      <span className="status-text">
                        {talentDetails.job_seeking_status === 'actively-seeking' ? 'Actively Seeking' : talentDetails.job_seeking_status.charAt(0).toUpperCase() + talentDetails.job_seeking_status.slice(1).replace(/-/g, ' ')}
                      </span>
                    </span>
                  )}
                  
                  {talentDetails.work_type && (
                    <span className="work-preference">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                        <line x1="8" y1="21" x2="16" y2="21"></line>
                        <line x1="12" y1="17" x2="12" y2="21"></line>
                      </svg>
                      {talentDetails.work_type.charAt(0).toUpperCase() + talentDetails.work_type.slice(1).replace('-', ' ')}
                    </span>
                  )}
                  
                  {talentDetails.location && (
                    <span className="work-preference">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                      {talentDetails.location}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="profile-main-content">
          <div className="content-layout">
            {/* Left Sidebar */}
            <div className="profile-sidebar">
              {/* Contact Info */}
              <div className="sidebar-card" style={{ boxShadow: 'none', border: '1px solid #e1e5e9' }}>
                <h3>Contact Information</h3>
                <div className="contact-info">
                  {talentDetails.email && (
                    <div className="contact-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                      <span>{talentDetails.email}</span>
                    </div>
                  )}
                  {talentDetails.phone && (
                    <div className="contact-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                      </svg>
                      <span>{talentDetails.phone}</span>
                    </div>
                  )}
                  {talentDetails.timezone_display && (
                    <div className="contact-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12,6 12,12 16,14"></polyline>
                      </svg>
                      <span>{talentDetails.timezone_display}</span>
                    </div>
                  )}
                  <div className="contact-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                      <path d="M22 7H2M7 2v4M17 2v4"></path>
                    </svg>
                    <span>Member since {formatDate(talentDetails.created_at)}</span>
                  </div>
                </div>
              </div>

              {/* Skills */}
              {talentDetails.skills && talentDetails.skills.length > 0 && (
                <div className="sidebar-card" style={{ boxShadow: 'none', border: '1px solid #e1e5e9' }}>
                  <h3>Skills</h3>
                  <div className="skills-list">
                    {talentDetails.skills.slice(0, 10).map((skill, index) => (
                      <div key={index} className="skill-tag">
                        {skill?.name || skill}
                      </div>
                    ))}
                    {talentDetails.skills.length > 10 && (
                      <div className="skill-tag more">
                        +{talentDetails.skills.length - 10} more
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Main Content Area */}
            <div className="profile-main">
              <div className="main-content">
                {/* About Section */}
                {talentDetails.bio && (
                  <div className="content-card" style={{ boxShadow: 'none', background: 'white', padding: '24px' }}>
                    <h3>About</h3>
                    <div className="summary-content">
                      <p>{talentDetails.bio}</p>
                    </div>
                  </div>
                )}

                {/* Experience Section */}
                <div className="content-card" style={{ boxShadow: 'none',  background: 'white', padding: '24px' }}>
                  <h3>Experience</h3>
                  <div className="public-profile experience-list">
                    {talentDetails.experiences && Array.isArray(talentDetails.experiences) && talentDetails.experiences.length > 0 ? (
                      talentDetails.experiences.map((exp, index) => (
                        <div key={index} className="experience-item">
                          <div className="experience-header">
                            <h4>{exp?.job_title || 'Unknown Position'}</h4>
                            <span className="experience-company">{exp?.company_name || 'Unknown Company'}</span>
                          </div>
                          <div className="experience-details">
                            <span className="experience-duration">
                              {formatDate(exp?.start_date)} - {formatDate(exp?.end_date)}
                            </span>
                            {exp?.location && (
                              <div className="experience-location">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                  <circle cx="12" cy="10" r="3"></circle>
                                </svg>
                                <span>{exp.location}</span>
                              </div>
                            )}
                          </div>
                          {exp?.description && (
                            <p className="experience-description">{exp.description}</p>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="placeholder-content">
                        <div className="placeholder-icon">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                          </svg>
                        </div>
                        <p className="placeholder-text">Build Your Professional Journey</p>
                        <p className="placeholder-subtext">Your work experience is one of the most important aspects of your profile. Adding your professional history demonstrates your career growth, industry expertise, and the value you bring to organizations. Complete your experience section to showcase your career achievements and attract better opportunities.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Projects Section */}
                <div className="content-card" style={{ boxShadow: 'none', background: 'white', padding: '24px' }}>
                  <h3>Projects</h3>
                  <div className="projects-list">
                    {talentDetails.projects && Array.isArray(talentDetails.projects) && talentDetails.projects.length > 0 ? (
                      talentDetails.projects.map((project, index) => (
                        <div key={index} className="project-item">
                          <div className="project-header">
                            <h4>{project?.title || 'Untitled Project'}</h4>
                            {project?.status && (
                              <span className={`project-status ${project.status.toLowerCase()}`}>
                                {project.status}
                              </span>
                            )}
                          </div>
                          {project?.description && (
                            <p className="project-description">{project.description}</p>
                          )}
                          {project?.technologies && Array.isArray(project.technologies) && project.technologies.length > 0 && (
                            <div className="project-technologies">
                              {project.technologies.map((tech, techIndex) => (
                                <span key={techIndex} className="tech-tag">{tech}</span>
                              ))}
                            </div>
                          )}
                          {project?.project_url && (
                            <a href={project.project_url} target="_blank" rel="noopener noreferrer" className="project-link">
                              View Project
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                <polyline points="15,3 21,3 21,9"></polyline>
                                <line x1="10" y1="14" x2="21" y2="3"></line>
                              </svg>
                            </a>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="placeholder-content">
                        <div className="placeholder-icon">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
                            <polyline points="2 17 12 22 22 17"></polyline>
                            <polyline points="2 12 12 17 22 12"></polyline>
                          </svg>
                        </div>
                        <p className="placeholder-text">Showcase Your Best Work</p>
                        <p className="placeholder-subtext">Projects are the proof of your skills and capabilities. Displaying your portfolio demonstrates your technical expertise, problem-solving abilities, and the real-world impact of your work. Add your projects to give visitors concrete examples of what you can achieve and stand out from the crowd.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Education Section - Last */}
                <div className="content-card" style={{ boxShadow: 'none', background: 'white', padding: '24px' }}>
                  <h3>Education</h3>
                  <div className="education-list">
                    {talentDetails.education && Array.isArray(talentDetails.education) && talentDetails.education.length > 0 ? (
                      talentDetails.education.map((edu, index) => (
                        <div key={index} className="education-item">
                          <div className="education-header">
                            <h4>
                              {edu?.degree || 'Degree'}
                              {edu?.field_of_study && ` (${edu.field_of_study})`} - {edu?.institution || 'Institution'}
                              <span className="education-duration">
                                {formatDate(edu?.start_date)} - {formatDate(edu?.end_date)}
                              </span>
                            </h4>
                          </div>
                          {edu?.description && (
                            <p className="education-description">{edu.description}</p>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="placeholder-content">
                        <div className="placeholder-icon">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                            <line x1="9" y1="7" x2="15" y2="7"></line>
                            <line x1="9" y1="11" x2="15" y2="11"></line>
                          </svg>
                        </div>
                        <p className="placeholder-text">Highlight Your Educational Foundation</p>
                        <p className="placeholder-subtext">Your educational background demonstrates your commitment to learning and your foundation of knowledge. Completing this section adds credibility to your profile and helps employers understand your academic qualifications. Share your educational journey to build trust and showcase your dedication to professional development.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bookmark Modal */}
      {isAuthenticated && !isOwnProfile && (
        <BookmarkModal
          isOpen={showBookmarkModal}
          onClose={() => setShowBookmarkModal(false)}
          talentId={talentDetails?.id}
          talentName={talentDetails?.name}
          onBookmarked={handleBookmarked}
        />
      )}

      {/* Report User Modal */}
      {isAuthenticated && !isOwnProfile && (
        <ReportUserModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          userId={talentDetails?.id}
          userName={talentDetails?.name}
          onSuccess={onUserReportSuccess}
        />
      )}

    </div>
  );
};

export default TalentDetailPanel;
